/**
 * Comic style presets.
 *
 * A style is a per-project "skin" that sets the default look of the whole page:
 * the paper, the panel inks, the speech-bubble lettering, and SFX styling.
 *
 * Items (cells / bubbles / sfx) only fall back to these defaults when they do
 * NOT have an explicit value of their own, so switching styles instantly
 * re-skins everything the user hasn't manually overridden — while custom
 * colors/fonts the user set by hand are preserved.
 */

export const COMIC_STYLES = [
  {
    key: "inked",
    label: "Classic Inked",
    // The vintage Marvel/DC look: cream newsprint, bold black inks,
    // crisp white bubbles with comic lettering, subtle Ben-Day dots.
    page: {
      background: "#f4ecd9", // warm cream newsprint
      frame: "3px solid #1a1a1a",
      shadow: "0 12px 34px rgba(0,0,0,0.28)",
      // Ben-Day halftone dots that show through in the gutters / paper margin
      halftone: { color: "rgba(26,26,26,0.07)", spacing: 6, radius: 1.3 },
      vignette: "radial-gradient(ellipse at center, rgba(0,0,0,0) 58%, rgba(0,0,0,0.07) 100%)",
    },
    panel: {
      borderColor: "#1a1a1a",
      borderWidth: 4,
      borderStyle: "solid",
      emptyFill: "#fffdf6",
    },
    bubble: {
      bgColor: "#ffffff",
      borderColor: "#1a1a1a",
      borderWidth: 2,
      fontFamily: "'Comic Neue', cursive",
      fontWeight: 700,
      textColor: "#1a1a1a",
      textAlign: "center",
      textTransform: "uppercase",
    },
    sfx: {
      color: "#ffe600",
      strokeColor: "#1a1a1a",
      fontFamily: "'Bangers', cursive",
    },
  },
  {
    key: "modern",
    label: "Modern Clean",
    // Contemporary graphic-novel / digital comic: pure white, heavy inks,
    // high contrast, no paper texture.
    page: {
      background: "#ffffff",
      frame: "1px solid #e6e6e6",
      shadow: "0 14px 44px rgba(0,0,0,0.18)",
      halftone: null,
      vignette: null,
    },
    panel: {
      borderColor: "#111111",
      borderWidth: 5,
      borderStyle: "solid",
      emptyFill: "#ffffff",
    },
    bubble: {
      bgColor: "#ffffff",
      borderColor: "#111111",
      borderWidth: 2.5,
      fontFamily: "'Comic Neue', cursive",
      fontWeight: 700,
      textColor: "#111111",
      textAlign: "center",
      textTransform: "none",
    },
    sfx: {
      color: "#ff3b30",
      strokeColor: "#111111",
      fontFamily: "'Bangers', cursive",
    },
  },
  {
    key: "manga",
    label: "Manga B&W",
    // Black-and-white manga: clean white page, fine black inks, hand lettering,
    // SFX as bold black-on-white.
    page: {
      background: "#ffffff",
      frame: "2px solid #000000",
      shadow: "0 10px 30px rgba(0,0,0,0.22)",
      halftone: null,
      vignette: null,
    },
    panel: {
      borderColor: "#000000",
      borderWidth: 3,
      borderStyle: "solid",
      emptyFill: "#ffffff",
    },
    bubble: {
      bgColor: "#ffffff",
      borderColor: "#000000",
      borderWidth: 2,
      fontFamily: "'Patrick Hand', cursive",
      fontWeight: 700,
      textColor: "#000000",
      textAlign: "center",
      textTransform: "none",
    },
    sfx: {
      color: "#111111",
      strokeColor: "#ffffff",
      fontFamily: "'Bangers', cursive",
    },
  },
  {
    key: "storyboard",
    label: "Storyboard",
    // A production board: plain white stock, thin black frame lines,
    // clean sans lettering so notes and dialogue read at a glance.
    page: {
      background: "#ffffff",
      frame: "1px solid #dcdcdc",
      shadow: "0 10px 30px rgba(0,0,0,0.16)",
      halftone: null,
      vignette: null,
    },
    panel: {
      borderColor: "#1a1a1a",
      borderWidth: 2,
      borderStyle: "solid",
      emptyFill: "#ffffff",
    },
    bubble: {
      bgColor: "#ffffff",
      borderColor: "#1a1a1a",
      borderWidth: 1.5,
      fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
      fontWeight: 400,
      textColor: "#1a1a1a",
      textAlign: "center",
      textTransform: "none",
    },
    sfx: {
      color: "#ffe600",
      strokeColor: "#1a1a1a",
      fontFamily: "'Bangers', cursive",
    },
  },
];

export const STORYBOARD_STYLE_KEY = "storyboard";

export const DEFAULT_COMIC_STYLE_KEY = "inked";

export const findComicStyle = (key) =>
  COMIC_STYLES.find((s) => s.key === key) || COMIC_STYLES[0];

/**
 * Build a CSS background-image / background-size pair that tiles Ben-Day
 * halftone dots, or null when the style has no halftone.
 */
export const halftoneBackground = (halftone) => {
  if (!halftone) return null;
  const { color, spacing, radius } = halftone;
  return {
    backgroundImage: `radial-gradient(${color} ${radius}px, transparent ${radius}px)`,
    backgroundSize: `${spacing}px ${spacing}px`,
  };
};
